import type { ReactNode } from "react";
import type { LinkComponentProps, LinkComponentType } from "../../../types/link";
import { DefaultLink } from "../../common/DefaultLink";
import { LgPhrase } from "../../../lg/LgPhrase";
import "./learn-card.css";

/** 札の中の段の 1 行 (トラックの札の「学習」「道場」の並び) */
export type LearnCardItem = {
  key: string;
  /** 行の頭の札 (学習、道場など) */
  tag: ReactNode;
  name: ReactNode;
  /** 名前の下の一言。切らずに折り返す */
  sub?: ReactNode;
  /** 上に細い線を引いて、前の行と分ける */
  sep?: boolean;
};

export type LearnCardProps = {
  to: string;
  /** アプリの中のリンク (react-router の Link など)。無ければ素の a */
  LinkComponent?: LinkComponentType;
  /** リンクに渡す追加の指定 (押した記録、先読み、経路の探索) */
  linkProps?: Pick<LinkComponentProps, "onClick" | "prefetch" | "discover">;
  /**
   * card は札ぜんたいが 1 つのリンク。title は題だけがリンクで、札の上に広げる
   * (札の中に別の釦があり、リンクを入れ子にできない札)
   */
  linkMode?: "card" | "title";
  /** title のリンクの読み上げの名前 */
  linkLabel?: string;
  /** tile は絵が上 (列に並べる札)、row は絵が左 (横長の札) */
  layout?: "tile" | "row";
  /** 札の絵。img か、絵のない物の代わりの面。無ければ窓ごと出さない */
  art?: ReactNode;
  /** cover は窓いっぱいに切って広げる。contain は縦横比がまちまちな図を切らずに収める */
  artFit?: "cover" | "contain";
  /** 絵の窓の縦横比。ogp は 1200 / 630 (コースの絵)、wide は 16 / 9 (隅に印を描いた場面の絵を切らない) */
  artAspect?: "ogp" | "wide";
  /** 題の上の札 (分類、順位など) */
  tags?: ReactNode;
  title: ReactNode;
  titleAs?: "h2" | "h3" | "h4";
  /** 3 行で切る説明 */
  text?: ReactNode;
  /** 説明を切らずに全文出す (横長の札を 1 列に並べる、レッスンの用語の欄) */
  textFull?: boolean;
  /** 説明に乗せたときに出す全文 */
  textTitle?: string;
  /** 説明の下の段の並び。5 行目からは出さず itemsMore で数だけ言う */
  items?: readonly LearnCardItem[];
  /** 段の並びで出さなかった数。「ほか n 個」 */
  itemsMore?: number;
  /** 出さなかった数の単位 (個、コースなど) */
  itemsMoreUnit?: string;
  /** 題と説明のあとに入れる札ごとの中身 */
  extra?: ReactNode;
  /** 札の下の行 (難易度、数、丸い矢印) */
  footer?: ReactNode;
  /** 見るだけの札 (HP の帯など)。リンクにせず、押せる札の浮きと光も付けない。見た目の形は同じ */
  preview?: boolean;
  className?: string;
};

/**
 * 学びの札の 1 つの型。絵の窓、分類の札、題、2 行の説明、段の並び、下の行の順に積む。
 * 色、角、線、字は --lg-* (/luagate のテーマ) を読み、無い画面では --color-* に落とす。
 * 浮き、絵のずれ、光は .lg の器 (LgCardScope) の側が持つ
 */
export function LearnCard({
  to,
  LinkComponent = DefaultLink,
  linkProps,
  linkMode = "card",
  linkLabel,
  layout = "tile",
  art,
  artFit = "cover",
  artAspect = "ogp",
  tags,
  title,
  titleAs: Title = "h3",
  text,
  textFull = false,
  textTitle,
  items,
  itemsMore = 0,
  itemsMoreUnit = "個",
  extra,
  footer,
  preview = false,
  className,
}: LearnCardProps) {
  const stretch = linkMode === "title" && !preview;
  const cls = [
    preview ? "lg-card lg-card--plain lg-card--pad-none" : "lg-card lg-card--plain lg-card--pad-none lg-card--interactive",
    stretch ? "lg-card--linked lgl-stretch" : "",
    "lgl-card",
    `lgl-card--${layout}`,
    artFit === "contain" ? "lgl-card--figure" : "",
    artAspect === "wide" ? "lgl-card--wide-art" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      {art ? <div className="lg-card-art lgl-art">{art}</div> : null}
      <div className="lgl-body">
        {tags ? <div className="lgl-tags">{tags}</div> : null}
        <Title className="lg-title lgl-title">
          {stretch ? (
            <LinkComponent to={to} {...linkProps} className="lgl-stretch__link" {...(linkLabel ? { "aria-label": linkLabel } : {})}>
              {typeof title === "string" ? <LgPhrase>{title}</LgPhrase> : title}
            </LinkComponent>
          ) : typeof title === "string" ? (
            <LgPhrase>{title}</LgPhrase>
          ) : (
            title
          )}
        </Title>
        {text ? (
          <p className={textFull ? "lgl-text lgl-text--full" : "lgl-text"} title={textTitle}>
            {typeof text === "string" ? <LgPhrase>{text}</LgPhrase> : text}
          </p>
        ) : null}
        {items && items.length > 0 ? (
          <ul className="lgl-items">
            {items.map((item) => (
              <li key={item.key} className={item.sep ? "lgl-items__row lgl-items__row--sep" : "lgl-items__row"}>
                {item.tag}
                <div className="lgl-items__text">
                  <p className="lgl-items__name"><LgPhrase>{item.name}</LgPhrase></p>
                  {item.sub ? <p className="lgl-items__sub"><LgPhrase>{item.sub}</LgPhrase></p> : null}
                </div>
              </li>
            ))}
            {itemsMore > 0 ? (
              <li className="lgl-items__more">
                ほか <span className="lg-tnum">{itemsMore}</span> {itemsMoreUnit}
              </li>
            ) : null}
          </ul>
        ) : null}
        {extra}
        {footer}
      </div>
    </>
  );

  if (stretch || preview) return <div className={cls}>{body}</div>;
  return (
    <LinkComponent to={to} {...linkProps} className={cls}>
      {body}
    </LinkComponent>
  );
}
